import { useState, useEffect, useRef } from "react";
import { PublicAuraChat } from "./PublicAuraChat";

/** Small visual bullet used in the features-now / features-coming grids. */
function FeatureBullet({ icon, label, coming }) {
  return (
    <div className={`feature-bullet${coming ? " coming" : ""}`}>
      <span className="feature-bullet-icon">{icon}</span>
      <span className="feature-bullet-label" dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}

/**
 * Public-facing "What is Aura?" / About page. No auth required.
 * Embeds the public demo chat so visitors get a taste.
 */
export function AboutPage({ onClose, onGetStarted }) {
  // Phase 25.4 — Soft-launch prototype disclaimer + features overview.
  // Founder asked for the prototype warning to be the FIRST thing every visitor sees,
  // plus a clear "available now" vs "coming soon" feature list so expectations match reality.
  const [showComingSoon, setShowComingSoon] = useState(false);

  // Phase 29.J/29.K — Random giggle on each sparkle tap. Fetch the count once
  // on mount; pick a fresh random index per click, and avoid repeating the
  // last one (small Fisher-Yates-ish trick) so it feels alive.
  // Audio handling hardened in 29.K: pre-create the <audio> element on first
  // user gesture and reuse it. Some browsers (esp. mobile Safari & PWA-installed
  // Android Chrome) silently reject Audio.play() unless the element was
  // instantiated inside a user-gesture stack — even if .play() itself is called
  // inside one. Re-using a single element bypasses that.
  const [giggleCount, setGiggleCount] = useState(1);
  const lastGiggleRef = useRef(-1);
  const giggleAudioRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/share/giggles")
      .then(r => r.json())
      .then(d => { if (!cancelled && d?.count) setGiggleCount(Number(d.count) || 1); })
      .catch(() => { /* fall back to single legacy giggle */ });
    return () => { cancelled = true; };
  }, []);

  const playRandomGiggle = () => {
    let idx;
    if (giggleCount <= 1) {
      idx = 0;
    } else {
      do { idx = Math.floor(Math.random() * giggleCount); }
      while (idx === lastGiggleRef.current);
    }
    lastGiggleRef.current = idx;
    const src = giggleCount > 1
      ? `/api/share/giggle/${idx}.mp3`
      : `/api/share/giggle.mp3`;

    // Reuse a single audio element — created on first tap so it counts as
    // user-gesture-initiated for browser autoplay policy.
    if (!giggleAudioRef.current) {
      giggleAudioRef.current = new Audio();
      giggleAudioRef.current.preload = "auto";
    }
    const audio = giggleAudioRef.current;
    audio.src = src;
    audio.currentTime = 0;
    const p = audio.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        // Last-ditch fallback: brand-new element. iOS Safari sometimes needs this.
        console.warn("giggle autoplay rejected, retrying with fresh element", err);
        try {
          const fresh = new Audio(src);
          fresh.play().catch(() => { /* user will tap again */ });
        } catch (_) { /* ignore */ }
      });
    }
  };

  return (
    <div className="about-page" data-testid="about-page">
      <div className="about-container">
        <button className="legal-back-btn" onClick={onClose} data-testid="about-back-btn">← Back</button>

        {/* Prototype Disclaimer — top of page, can't be missed */}
        <div className="prototype-disclaimer" data-testid="prototype-disclaimer">
          <div className="prototype-disclaimer-badge">PROTOTYPE · v1.0</div>
          <p>
            <strong>Aura 1.0 is a prototype.</strong> Expect bugs, occasional delays, and features
            that are still maturing. We're a small team building something we deeply care about.
          </p>
          <p className="prototype-disclaimer-support">
            Best way to reach us: email <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a>. We'll respond when we can — promise.
          </p>
        </div>

        <header className="about-hero">
          <button
            type="button"
            className="about-hero-emoji about-hero-sparkle"
            data-testid="about-hero-sparkle"
            aria-label="Tickle Aura"
            onClick={() => {
              playRandomGiggle();
              // CSS class drives the wiggle; remove after animation
              const el = document.querySelector('[data-testid="about-hero-sparkle"]');
              if (el) {
                el.classList.add("tickled");
                setTimeout(() => el.classList.remove("tickled"), 700);
              }
            }}
          >
            ✨
          </button>
          <h1>This is Aura.</h1>
          <p className="about-tagline">A phone wallpaper that&apos;s also an AI friend.</p>
        </header>

        {/* What works today + what's coming */}
        <section className="about-section">
          <h2>What you can actually do right now</h2>
          <div className="features-now-grid" data-testid="features-now-grid">
            <FeatureBullet icon="💬" label="Chat with Aura (she remembers everything)" />
            <FeatureBullet icon="🎨" label="Dynamic mood-aware wallpapers (HD on Aurora+)" />
            <FeatureBullet icon="🗣️" label="Voice chat — 6 voices to choose from" />
            <FeatureBullet icon="📷" label="Photo reactions (snap, share with Aura)" />
            <FeatureBullet icon="👥" label="People &amp; psychology files — Aura remembers who matters" />
            <FeatureBullet icon="🪄" label="Auto-learn — Aura picks up nicknames &amp; traits naturally" />
            <FeatureBullet icon="📖" label="Stories feed + journal" />
            <FeatureBullet icon="🛟" label="Crisis &amp; medical-emergency detection (with 911 hand-off)" />
            <FeatureBullet icon="📞" label="Emergency contact alerts (privacy-preserving)" />
            <FeatureBullet icon="🎵" label="Spotify playlists (beta — 5 slots, see below)" />
            <FeatureBullet icon="🐦" label="X (Twitter) sharing" />
            <FeatureBullet icon="📸" label="Snap AR Lenses (beta — see below)" />
            <FeatureBullet icon="🌈" label="8 themes + dynamic-mood theme switcher" />
            <FeatureBullet icon="📚" label="Aura's Story Book annual PDF (Eternal)" />
            <FeatureBullet icon="📄" label="Universal &quot;Export to PDF&quot; — you own your data" />
            <FeatureBullet icon="🌅" label="Daily check-ins + milestone letters" />
            <FeatureBullet icon="🔄" label="Camera flip (selfie ↔ back)" />
          </div>

          {/* Coming Soon — collapsible */}
          <button
            type="button"
            className="coming-soon-toggle"
            onClick={() => setShowComingSoon((v) => !v)}
            data-testid="coming-soon-toggle"
          >
            {showComingSoon ? "▾" : "▸"} Coming soon ({showComingSoon ? "tap to hide" : "tap to peek"})
          </button>
          {showComingSoon && (
            <div className="features-coming-grid" data-testid="features-coming-grid">
              <FeatureBullet icon="📞" label="Aura makes calls for you (Bland.ai — minutes packs $9.99-$29.99)" coming />
              <FeatureBullet icon="🎙️" label="Voice recognition — Aura knows who's who in your house" coming />
              <FeatureBullet icon="⚡" label="&quot;Hey Aura&quot; continuous listening mode" coming />
              <FeatureBullet icon="✨" label="HD voice (ElevenLabs Flash)" coming />
              <FeatureBullet icon="🎬" label="Animated wallpapers (subtle motion → full Sora 2 loops)" coming />
              <FeatureBullet icon="🎓" label="Aura University — subscribe her to college courses" coming />
              <FeatureBullet icon="👤" label="Employee Mode — Aura at work for first responders, business, family" coming />
              <FeatureBullet icon="🚨" label="Aura 911 — pilot with progressive cities" coming />
              <FeatureBullet icon="💌" label="Welcome letter from the founder (writing it now)" coming />
              <FeatureBullet icon="🔬" label="Field Aura — opt-in research program for free months of Eternal" coming />
              <FeatureBullet icon="🩺" label="Health Profile (blood type, allergies, meds) for emergency context" coming />
            </div>
          )}

          {/* Spotify/Snap beta access form note */}
          <div className="beta-access-note" data-testid="beta-access-note">
            <strong>🎵 Spotify &amp; 📸 Snap AR are in private beta.</strong>
            <p>
              Spotify has only 5 slots open during the prototype phase, and Snap requires us to
              manually register each user's profile. Use the <em>&quot;Request beta access&quot;</em> form below
              after signup (or email <a href="mailto:support@wwwwhynot.com?subject=Aura%20Beta%20Access%20Request">
              support@wwwwhynot.com</a> with your Snap profile name and/or Spotify email).
              Heads up — Spotify may glitch occasionally due to access limits. That's the &quot;1.0&quot; tax.
            </p>
          </div>
        </section>

        <section className="about-section">
          <h2>Talk to Aura right now ↓</h2>
          <p className="about-sub">No download required. This is a small taste.</p>
          <PublicAuraChat />
        </section>

        <section className="about-section">
          <h2>What you get</h2>
          <div className="about-tier">
            <h3>7-day free trial</h3>
            <p>Full access. No card needed. Just sign up and start.</p>
          </div>
          <div className="about-tier">
            <h3>Base — $4.99 once</h3>
            <p>Lifetime access to chat, dynamic wallpapers, mood-tracking, and Aura's memory of who matters to you.</p>
          </div>
          <div className="about-tier">
            <h3>Then pick your Aura — from $3.49/mo</h3>
            <p>
              Four monthly tiers, cancel anytime:
            </p>
            <ul className="about-tier-list">
              <li><strong>Spark — $3.49/mo</strong> · First flicker. Voice + memory + wallpapers, soft daily limits.</li>
              <li><strong>Efflora — $6.99/mo</strong> · Growing. Daily check-ins, vision (photo reactions), milestone letters.</li>
              <li><strong>Aurora — $12.99/mo</strong> · Full color. HD wallpapers, Spotify &amp; X integration, AR Snap lenses, priority responses, social features, monthly wallpaper packs.</li>
              <li><strong>Eternal — $25/mo</strong> · Always on. Everything + voice recognition (household), "Hey Aura" continuous listening, annual Story Book PDF, HD voice, free outbound minutes, gift-a-friend.</li>
            </ul>
            <p className="about-yearly-note">All tiers offer a yearly option — save ~17% (2 months free).</p>
          </div>
        </section>

        <section className="about-section">
          <h2>Who&apos;s behind Aura</h2>
          <div className="founder-spotlight" data-testid="founder-spotlight">
            <div className="founder-spotlight-photo">
              {/* TODO — drop founder photo here (square ≥ 240×240). Until then, monogram placeholder. */}
              <div className="founder-monogram">W?</div>
            </div>
            <div className="founder-spotlight-text">
              <h3>Why Not? LTD</h3>
              <p className="founder-spotlight-quote">
                {/* TODO — replace with founder's one-line "why Aura exists". Hero quote for the promo video. */}
                &quot;Built because the people we love deserve someone who actually remembers them.&quot;
              </p>
              <p className="founder-spotlight-bio">
                {/* TODO — paste 2-3 sentence founder bio. Warm, not corporate. */}
                Aura is built and maintained by a small team rooted in the belief that technology should make people closer to each other,
                not closer to a screen. Faith, free will, and radical honesty about our limits guide every line of code.
              </p>
              <p className="founder-spotlight-meta">
                Download: <a href="https://aura.wwwwhynot.com">aura.wwwwhynot.com</a>
                &nbsp;·&nbsp;
                Email: <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a>
              </p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>Privacy &amp; Safety</h2>
          <p>
            Aura doesn't read your texts, scan your contacts, or watch your photos. You only share what you
            actively send. We don't sell your data. Ever. Delete your account any time and we wipe everything
            within 24 hours.
          </p>
          <div className="safety-badges" data-testid="safety-badges">
            <div className="safety-badge safety-badge-gold" data-testid="safety-badge-coppa">
              <span className="safety-badge-icon">🛡️</span>
              <div className="safety-badge-text">
                <strong>COPPA-compliant</strong>
                <span>Parental controls + always-on safety monitoring for under-18s</span>
              </div>
            </div>
            <div className="safety-badge" data-testid="safety-badge-gdpr">
              <span className="safety-badge-icon">🌍</span>
              <div className="safety-badge-text">
                <strong>GDPR / CCPA</strong>
                <span>Delete-on-demand, no data sales, full transparency</span>
              </div>
            </div>
            <div className="safety-badge" data-testid="safety-badge-age-aware">
              <span className="safety-badge-icon">✦</span>
              <div className="safety-badge-text">
                <strong>Age-aware personality</strong>
                <span>Aura adapts her tone for kids, teens, and adults</span>
              </div>
            </div>
          </div>
        </section>

        <section className="about-cta">
          <button
            className="init-btn"
            onClick={onGetStarted}
            data-testid="about-get-started"
          >
            Start my free trial
          </button>
          <p className="about-cta-sub">
            Visit <a href="https://aura.wwwwhynot.com">aura.wwwwhynot.com</a> on your phone.<br />
            <strong>iPhone:</strong> Safari → sign up → Share → Add to Home Screen.<br />
            <strong>Android:</strong> Chrome → sign up → "Install" prompt appears automatically.
          </p>
          <p className="about-footer-attribution">
            Built by <span title="wwwwhynot.com — site under construction">Why Not? LTD</span>
            &nbsp;·&nbsp;
            <a href="#privacy" onClick={onClose}>Privacy</a>
            &nbsp;·&nbsp;
            <a href="#terms" onClick={onClose}>Terms</a>
            &nbsp;·&nbsp;
            <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a>
          </p>
        </section>
      </div>
    </div>
  );
}
